import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  signal,
  input,
  output,
  effect,
  untracked,
} from '@angular/core';
import { ToastService } from 'src/app/services/toast.service';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { AuthService, LoginMethod } from 'src/app/services/Auth/auth.service';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { previewMap } from './previewMap';

import { FormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ObjTypePipe as objtype } from 'src/app/pipes/objtype.pipe';
import { MangelMediaApiFuncService } from 'src/app/services/MediaApiFunc/mangel-media-api-func.service';
import {
  FileForMediaView,
  MediaViewModalComponent,
  TitleAndFileNames,
} from '../_shared/mediaviewmodal/mediaviewmodal.component';
import { ElementMediaQueryDirective } from 'src/app/directives/element-media-query.directive';
import { TabsInOverflowDirective } from 'src/app/directives/tabs-in-overflow.directive';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrUnitTextPipe } from 'src/app/pipes/mrUnitText.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { SonderinspektionmodalComponent } from '../_modals/sonderinspektionmodal/sonderinspektionmodal.component';
import { ImageSlideShowComponent } from '../_shared/media-slide-show/image-slide-show.component';
import { PaintingComponent } from '../_shared/paint/painting.component';
import { PlotlyPlotComponent } from '../plotly-plot/plotly-plot.component';
import { HerstellDatenModalComponent } from './herstell-daten-modal/herstell-daten-modal.component';
import { HistorieTreeComponent } from './historie-tree/historie-tree.component';
import { MangelLogModalComponent } from './mangel-log-modal/mangel-log-modal.component';
import { ClarityModule, ClrDatagrid } from '@clr/angular';
import { CommonModule, KeyValue } from '@angular/common';
import { WindowMediaQueryDirective } from 'src/app/directives/window-media-query.directive';
import { ZaehlerAccordionComponent } from './zaehler-accordion/zaehler-accordion.component';
import { MessungGridComponent } from '../_tabs/messung-grid/messung-grid.component';
import { AnlagenMediaApiFuncService } from 'src/app/services/MediaApiFunc/anlagen-media-api-func.service';
import { IsRep } from 'src/app/services/_offline/dexieDB';
import { DetailViewResponse } from './detailview.types';
import { FileForPreview } from '../_shared/preview-thumbnails/preview-thumbnail.type';
import { Row, Table } from 'src/app/models/ui/table';
import { getSpezValue, getStammValue, getTokenRight } from 'src/app/misc/functions';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { LogbuchComponent } from './logbuch/logbuch.component';
import { StreckenbilderComponent } from './streckenbilder/streckenbilder.component';
import { TextBoolDgCell } from '../_shared/datagrid-cell/text-bool-dg-cell';
import { IconDgCellComponent } from '../_shared/datagrid-cell/icon-dg-cell.component';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { TrassierungComponent } from './trassierung/trassierung';
import { PrüfungsTypStore } from 'src/app/services/Shared/prüfung-typ.store';

interface TreeDataNode {
  id: number;
  typ: number;
  name: string;
  selected: boolean;
  expanded: boolean;
  rootpath: string | null;
  parent: TreeDataNode | null;
  children: TreeDataNode[];
}

const MAX_MESSUNGEN_PRUEFUNGEN = 50;

function toColor(num) {
  num >>>= 0;
  var b = num & 0xff,
    g = (num & 0xff00) >>> 8,
    r = (num & 0xff0000) >>> 16;
  return 'rgb(' + [r, g, b].join(',') + ')';
}

export interface DetailViewAnsicht {
  mode: 'close' | 'half' | 'full' | 'progress' | 'hidden';
  val: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-detailview',
  templateUrl: './detailview.component.html',
  styleUrls: ['./detailview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    PlotlyPlotComponent,
    PaintingComponent,
    ImageSlideShowComponent,
    MediaViewModalComponent,
    HistorieTreeComponent,
    MangelLogModalComponent,
    SonderinspektionmodalComponent,
    HerstellDatenModalComponent,
    ZaehlerAccordionComponent,
    MessungGridComponent,
    ElementMediaQueryDirective,
    WindowMediaQueryDirective,
    TabsInOverflowDirective,
    TablePrettyPrintPipe,
    MrUnitTextPipe,
    HideIDColumnsPipe,
    ArrayMethodsCallbackPipe,
    objtype,
    MrTranslatePipe,
    LogbuchComponent,
    StreckenbilderComponent,
    TextBoolDgCell,
    IconDgCellComponent,
    TrassierungComponent,
  ],
})
export class DetailviewComponent implements AfterViewInit, OnDestroy {
  private readonly apiService = inject(APIService);
  protected readonly authService = inject(AuthService);
  private readonly basicStore = inject(BasicStoreService);
  private readonly overlayService = inject(OverlayService);
  private readonly spinner = inject(SpinnerService);
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  protected readonly media = inject(MangelMediaApiFuncService);
  protected readonly bauwmedia = inject(AnlagenMediaApiFuncService);
  protected readonly pruefungsTyp = inject(PrüfungsTypStore);
  private readonly confirmService = inject(ConfirmService);

  private readonly entsperrService = inject(EntsperrAnlageService);

  //@Input('sidebar') sidebar :MatSidenav;
  //@Input('PBenlarged') progressbarenlarged:boolean;
  @Input('progressbarenlarged') progressbarenlarged: boolean;
  @ViewChild('kategorie') kategorie: ElementRef;
  @ViewChild('dgspez') dgspez: ClrDatagrid;
  @ViewChild(SonderinspektionmodalComponent)
  modal: SonderinspektionmodalComponent;

  constructor() {
    dayjs.locale('de');
    dayjs.extend(customParseFormat);
    dayjs.extend(isSameOrAfter);
    let token = this.authService.getToken();
    if (token && token.rights) {
      this.showProtected = {
        assignment: getTokenRight(token.rights.program, 'AllowPAuftrCreate'),
        feedback: true,
        history: getTokenRight(token.rights.view, 'AllowVHIST'),
        interference: getTokenRight(token.rights.view, 'AllowVSTOE'),
        speedRestrictions: getTokenRight(token.rights.view, 'AllowVLAFS'),
        blockings: getTokenRight(token.rights.view, 'AllowVSPER'),
        kav: getTokenRight(token.rights.view, 'AllowVHIST'),
      };
      this.database = token.db;
      this.progVersion = parseInt(token.rights.progVersion);
      this.userstatus = token.rights.status;
    }

    effect(() => {
      if (this.KPRUVID() && this._ostammid) untracked(() => this.loadAnlageById(this._ostammid));
    });
  }

  loading: BehaviorSubject<boolean> = new BehaviorSubject(true);
  isRep = IsRep();
  tables: Record<string, any> = {};
  bezeichnung: string;
  lpDatum: any;

  protected files$: Observable<FileForPreview[]>;
  protected skizzeData: FileForPreview;
  protected skizzeName: string;
  protected messungsOPRUEFID: number;
  protected strBild: string;

  progVersion: number = 50;

  docheader: any[] = [];
  dokheaderAnzahlItems: any[] = [];
  docsorginal: Table = TABLE_EMPTY();
  docs: Table = TABLE_EMPTY();
  objOpt: any;
  seldoctyp: number = 0;
  selAnzDok: number = 0;
  katStatus: string = '';
  showKAV: boolean = false;
  showGIS: boolean = false;
  map: any;
  userstatus: number = 1;
  alv_date: any;
  alv_name: string = '';
  ebl_date: any;
  ebl_name: string = '';

  database: string;
  protected readonly mangelLog = signal<number>(undefined);
  mangelMedia: TitleAndFileNames[];
  showMangelMedia: boolean = false;
  anlagenMedia: TitleAndFileNames[];
  bauwerksMedia: TitleAndFileNames[];
  protected dokumentenMedia = signal<FileForMediaView>(undefined);

  isHerstellDatenVisible = false;
  protected selectedSpezID: number;

  hasOpenMangel: boolean = false;
  hasMangel: boolean = false;
  hasMessungen: boolean = false;
  deactivateAll: boolean = false;
  deactivateRep: boolean = false;
  excelPath = undefined;

  PRDEL: boolean = false;
  STDEL: boolean = false;
  STEDIT: boolean = false;
  STCOPY: boolean = false;
  SPEDIT: boolean = false;
  BWEDIT: boolean = false;
  KOMEDIT: boolean = false;
  TRAEDIT: boolean = false;
  DOCEDIT: boolean = false;

  @Output() enlarge = new EventEmitter<DetailViewAnsicht>();
  @Output() enlargePB = new EventEmitter<boolean>();
  @Output() progressData = new EventEmitter<any>();
  @Output() indexScrub = new EventEmitter<number>();
  @Output() reload = new EventEmitter();
  public readonly gispos = output<number>();
  reloadTabsIsReady = new Subject();

  GroupBemerkung = new UntypedFormGroup({
    bemerkung: new UntypedFormControl(''),
    skizzenname: new UntypedFormControl(''),
  });

  /**
   * True when user is logged in via Okta and belongs only to external/customer groups
   * (no internal or admin groups). Used to disable creating new inspections for customers.
   */
  public isExternalOnlyCustomer(): boolean {
    return this.authService?.isExternalOnlyOktaUser() ?? false;
  }

  /**
   * FAT UI limitation flag for detailview actions.
   * Returns true only for external-only Okta customers (no internal/admin groups).
   * Admin and internal Okta users can edit Stammdaten, Spezifikation, Herstelldaten.
   * External users get read-only access to these dialogs.
   */
  public get isFatUiLimited(): boolean {
    return this.authService?.isExternalOnlyOktaUser() ?? false;
  }

  public changeGisPos(event) {
    this.gispos.emit(event * 1000);
  }

  public readonly isFatObject = computed(
    () => this.authService.loginMethod$() == LoginMethod.Okta && this.otypid === 1
  );

  //expanded: boolean;
  @Input('split50') split50: boolean = false;
  @Input('expanded') expanded: boolean = false;
  @Input('hidden') set hidden(value: boolean) {
    this.isShownDetailView.set(!value);
  }
  readonly viewMode = input<number>(0); //Ansichtsmodus, ändert je nach Typ --- 0 = Standard, 1 = Alle Buttons deaktivieren
  dataRefresh = new EventEmitter();
  refreshSubscription: Subscription;
  trackByOmazsID = (_: number, row: any) => row['OMAZSID'];
  protected readonly isShownDetailView = signal(true);
  protected toggleDetailViewIsShown = () => {
    this.isShownDetailView.update(shown => !shown);
    this.enlarge.emit({ mode: 'hidden', val: !this.isShownDetailView() });
  };

  protected getRestTabellen = ({ key }: KeyValue<string, any>) =>
    ![
      'stamm',
      'messungen',
      'zaehler',
      'historie',
      'herst',
      'mangel',
      'komp',
      'spez',
      'komponenten',
      'dokheader',
      'doks',
      'dokheaderAnzahlItems',
      'anlagenstatistik',
    ].includes(key);

  isKatVisible: boolean = false;
  TitelKat: string = 'Neue Kategorie';
  _ostammid: number;
  geometry: any;
  typ_Line_Point: number;
  otypid: number;
  pointtype: number = undefined;
  bemerkung: any;
  bemerkung_pruefung: any;
  selectedSpez: any;

  /**
   * Returns true if a valid Spezifikation is available for Herstelldaten.
   * For line objects: requires a row to be selected in the datagrid.
   * For point objects: requires the spez data to contain a valid OSPEZID.
   */
  protected get hasSelectedSpez(): boolean {
    if (!this.selectedSpez) return false;
    const isLine = objtype.isLine(this.typ_Line_Point);
    if (isLine) {
      return !!this.selectedSpez?.OSPEZID?.value;
    } else {
      // Point object: selectedSpez is an array, check for OSPEZID entry
      return !!getSpezValue(Array.isArray(this.selectedSpez) ? this.selectedSpez : undefined, 'OSPEZID');
    }
  }

  protected activeTab: { [key: string]: boolean } = {};
  protected initTab: { [key: string]: boolean } = {};

  bogenLayout: any = {
    autosize: true,
    showlegend: false,
    height: 600,
    margin: {
      l: 35,
      r: 20,
      b: 25,
      t: 20,
      pad: 0,
    },
    plot_bgcolor: 'transparent',
    paper_bgcolor: 'transparent',
    yaxis: {
      //mirror: true
    },
  };

  graph: any = {
    layout: {
      autosize: true,
      showlegend: true,
      legend: { orientation: 'h' },
      height: 730,
      margin: {
        l: 35,
        r: 20,
        b: 25,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: ['sendDataToCloud', 'lasso2d'],
    },
    m: [],
    show: {
      bogen: true,
      spur: true,
      tiefpunkt: true,
      ueberhoehung: true,
      verwindung: true,
      rille_l: true,
      rille_r: true,
      dh_l: false,
      ds1_l: false,
      ds2_l: false,
      dh_r: false,
      ds1_r: false,
      ds2_r: false,
      fahrdrahtx: false,
      fahrdrahty: false,
    },
    y: [],
    yData: {
      bogen: [],
      sollspur: [],
      spur: [],
      tiefpunkt: [],
      ueberhoehung: [],
      verwindung1: [],
      verwindung2: [],
      rille_l: [],
      rille_r: [],
      dh_l: [],
      ds1_l: [],
      ds2_l: [],
      dh_r: [],
      ds1_r: [],
      ds2_r: [],
    },
  };

  showProtected: any;

  kav: any = {
    value: undefined,
    color: undefined,
  };
  hasProfilmessung: boolean = false;
  hasGleismessung: boolean = false;
  hasStellkraftmessung: boolean = false;
  hasWebcamImages: boolean = false;
  hasRiffelMessung: boolean = false;
  hasTrassierung: boolean = false;
  optInterface: number = 0;

  rights: any;

  opruefid: number = 0;
  _kavaktiv: boolean = false;

  komponentenObjekt: any;
  komponentenObjektSpez: any;
  komponentenObjektBilder: any;
  komponentenObjektHist: any;

  teilerneuerungObjektT: any;
  teilerneuerungObjektS: any;

  rootObjekt: TreeDataNode;
  rootname: string;
  spezData: any = [];
  kompHistoryData: any = [];
  selOKOMPID: number = 0;
  selnode: any;
  selnodename: string = '';
  selnodetyp: number = 0;
  bilder: any[] = [];
  allSubNodes: any = [];

  showbemerkung: boolean = false;
  mangelbemerkung: string = '';
  curID: number = 0;

  anlagenstatistik: any = [];
  graphkavelemente: any[];

  kavstatistic: any = [];
  kavelemente: any = [];
  fkninsp: any = [];
  fknih: any = [];
  dia_fkninsp: any = [];
  dia_fknih: any = [];
  dia_kav_spez: any = [];
  ih_average: any = [];
  ih_qoute: any = [];
  dia_ih_average: any;
  dia_ih_qoute: any;
  baseStartMeter: number;
  baseEndMeter: number;
  trassierungtable: Table = TABLE_EMPTY();

  graphkav0: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: { range: [0, 1] },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkavdonat: any = {
    layout: {
      title: {
        font: {
          size: 12,
        },
        text: '',
      },

      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 10,
        r: 10,
        b: 10,
        t: 30,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      annotations: [
        {
          showarrow: false,
          text: '',
          x: 0.5,
          y: 0.5,
        },
      ],
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphfk: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      barmode: 'group',
      bargroupgap: 0.15,
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  set kavaktiv(newVal: any) {
    let val: any =
      newVal === true ? true
      : newVal === false ? false
      : newVal.target.checked;
    if (newVal?.target) {
      this.apiService
        .sendObjektKAV(this._ostammid, val)
        .pipe(first())
        .subscribe((res: any) => {
          if (res?.status == 'OK') {
            this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
          }
        });
    }

    this._kavaktiv = val;
  }
  get kavaktiv() {
    return this._kavaktiv;
  }

  @ViewChild('chart', { static: true }) chart: TemplateRef<any>;
  @ViewChild(ClrDatagrid) grid: ClrDatagrid;

  protected readonly rechte = input(1, { transform: (val: unknown) => (val as number) || 2 });

  @Input() set ostammid(id: number) {
    if (id) {
      this.loading.next(true);
      this.kav.value = undefined;
      this.kav.color = undefined;
      this.selectedSpez = undefined;
      this.showKAV = false;
      this.hideColumnsArr = ['bild'];
      this.spinner.enable();

      this.loadAnlageById(id);
      this._ostammid = id;
    }

    this.bilder = [];
    this.spezData = [];
    this.kompHistoryData = [];
  }

  readonly kpruvid = input<number>(undefined);
  private readonly KPRUVID = computed(() => this.kpruvid() || this.pruefungsTyp.KPRUVID());
  ErsterEintragMessung: any;

  calKAV() {
    this.apiService
      .berechneKAV(this._ostammid)
      .pipe(first())
      .subscribe((res: any) => {
        if (res?.status == 'OK') {
          const message = Object.entries(res.items.rows[0])
            .map(([k, v]) => `${k}: ${Number(v).toFixed(3)}`)
            .join('<br>');

          this.toastr.success(message, 'KAV ' + res.kav, {
            enableHtml: true,
          });
        }
      });
  }

  async loadAnlageById(id: number) {
    this.rights = JSON.parse(this.authService.decrypt(localStorage.getItem('rights')));
    this.deactivateAll = this.authService?.token?.rights?.status <= 2 || this.viewMode() == 1;
    this.initTab = {};
    try {
      const result = await firstValueFrom(this.apiService.getAnlage(id, this.KPRUVID()));
      if (!result) throw new Error('Kein Resultat');
      if(result.status == "error") {
        this.spinner.disable();
        this.toastr.error(this.mrTranslate.transform(result.text));
        this.closeHandler();
      }
      this.getAnlageByID(result);
    } catch (ex) {
      console.log(ex);
      this.spinner.disable();
      this.toastr.error(this.mrTranslate.transform('Etwas ist schief gelaufen'));
      this.closeHandler();
      return;
    }
  }

  getAnlageByID(result: DetailViewResponse) {
    this.showKAV = result.showkav;
    this.hasStellkraftmessung = result.hasStellkraftmessung ?? false;
    this.hasOpenMangel = false;
    this.hasMangel = false;
    this.hasMessungen = false;
    this.opruefid = result.opruefid;
    this.excelPath = undefined;
    this.tables = {};
    this.pointtype = undefined;
    this.bemerkung = result.bemerkung;
    this.bemerkung_pruefung = result.bemerkung_pruefung;
    this.deactivateRep = result.isRepLocked;
    this.files$ = of(null);
    this.GroupBemerkung.controls.bemerkung.setValue(result.bemerkung);
    this.hasWebcamImages = result.haswebcamimage;
    this.hasRiffelMessung = result.hasriffelmessung;
    this.hasTrassierung = result.hastrassierung;
    this.optInterface = +this.authService.decrypt(localStorage.getItem('optInterface'));

    if (!getTokenRight(this.authService.token.rights.program, 'AllowPINTEX')) {
      this.optInterface = 0;
    }

    if (result.otypid) {
      this.typ_Line_Point = result.typ_Line_Point;
      this.otypid = result.otypid;
    } else {
      this.otypid = 0;
    }

    this.alv_date = result.alv_date;
    this.alv_name = result.alv_name;
    this.ebl_date = result.ebl_date;
    this.ebl_name = result.ebl_name;

    Object.keys(this.rights.type).forEach(typid => {
      if (typid == this.otypid.toString()) {
        this.PRDEL = getTokenRight(this.rights.type[typid], 'PRDEL');
        this.STDEL = getTokenRight(this.rights.type[typid], 'STDEL');
        this.STEDIT = getTokenRight(this.rights.type[typid], 'STEDIT');
        this.STCOPY = getTokenRight(this.rights.type[typid], 'STCOPY');
        this.SPEDIT = getTokenRight(this.rights.type[typid], 'SPEDIT');
        this.BWEDIT = getTokenRight(this.rights.type[typid], 'BWEDIT');
        this.DOCEDIT = getTokenRight(this.rights.view, 'AllowVDOKV');
        this.KOMEDIT = getTokenRight(this.rights.type[typid], 'KOMEDIT');
        this.TRAEDIT = getTokenRight(this.rights.type[typid], 'TRAEDIT');
      }
    });

    this.pointtype = result.pointtype;
    this.objOpt = result.objOpt;

    if (result.tabs) {
      if (objtype.isPoint(this.typ_Line_Point)) this.selectedSpez = result.tabs.spez;

      Object.keys(result.tabs).forEach(index => {
        this.tables[index] ??= {};
        let elem = result.tabs[index];
        switch (index) {
          case 'dokheader':
            this.docheader = elem;
            this.tables['dokheader'] = { calcLength: true };
            break;
          case 'dokheaderAnzahlItems':
            this.dokheaderAnzahlItems = elem;
            break;
          case 'doks':
            this.docsorginal = elem;
            this.docs.columns = elem.columns;
            this.docs.rows = elem.rows.filter(item => item.Kategorie == 0);

            this.hideColumnsArr.push('OTypid');
            this.hideColumnsArr.push('Kategorie');
            this.selAnzDok = this.dokheaderAnzahlItems[0];

            break;
          case 'trassierung':
            this.trassierungtable = elem;
            break;
          case 'komponenten':
            this.komponentenObjekt = elem.objektkomponenten;
            this.komponentenObjektSpez = elem.spez;
            this.komponentenObjektBilder = elem.bilder;
            this.komponentenObjektHist = elem.historie;

            break;
          case 'teilerneuerung':
            this.teilerneuerungObjektT = elem.teilerneuerung;
            this.teilerneuerungObjektS = elem.schweissung;

            this.tables['teilerneuerung'].rows = elem.teilerneuerung.rows;
            this.tables['teilerneuerung'].columns = elem.teilerneuerung.columns;

            if (elem.schweissung != null) {
              this.tables['schweißungen'] = {};
              this.tables['schweißungen'].rows = elem.schweissung.rows;
              this.tables['schweißungen'].columns = elem.schweissung.columns;
            }

            break;

          case 'stamm':
            let stamm = elem.rows.filter((elem: any) => {
              let obj = elem.Objekt;
              if (obj == 'Typ') {
                this.bezeichnung = elem.Bezeichnung;
              }
              if (obj == 'Bezeichnung') {
                this.bezeichnung += ' ' + elem.Bezeichnung;
                this.rootname = elem.Bezeichnung;
              }

              if (obj == 'Bezeichnung_Format') {
                if (elem.Bezeichnung !== null) this.bezeichnung = elem.Bezeichnung;
              } else if (obj == 'OSTAMMID') {
                elem.Objekt = 'StammID';
                return true;
              } else if (obj == 'Startmeter') {
                this.baseStartMeter = elem.Bezeichnung;
                return true;
              } else if (obj == 'Endmeter') {
                this.baseEndMeter = elem.Bezeichnung;
                return true;
              } else if (obj == 'letzte Prüfung') {
                this.lpDatum = elem.Bezeichnung;
                return true;
              } else if (obj == 'ExcelPath') {
                this.excelPath = elem.Bezeichnung;
                this.excelPath = elem.Bezeichnung;
              } else if (obj == 'KAV') {
                if (typeof elem.Bezeichnung == 'string' && elem.Bezeichnung.indexOf('%') != -1)
                  this.kav.value = elem.Bezeichnung;
                else if (elem.Bezeichnung === null) this.kav.value = 'NULL';
                else this.kav.value = elem.Bezeichnung;
              } else if (obj == 'KAVFarbe') {
                this.kav.color = elem.Bezeichnung;
              } else if (obj == 'KAVAktiv') {
                this.kavaktiv =
                  elem.Bezeichnung ?
                    elem.Bezeichnung == 1 || elem.Bezeichnung == true ?
                      true
                    : false
                  : false;
              } else if (obj == 'Bild') {
                this.strBild = elem.Bezeichnung;
                this.files$ = this.apiService.getPassBilder(elem.Bezeichnung);
                return false;
              } else if (obj == 'Skizze') return false;
              else return true;
              return false;
            });
            this.tables['stamm'].rows = [];
            setTimeout(() => {
              this.tables['stamm'].rows = stamm;
            });
            this.tables['stamm'].columns = elem.columns;
            break;
          case 'messungen':
            const table: Table = elem;
            const columns = table.columns;

            const count = columns.length - 18; /* standarde Spalten */
            const maxCount = MAX_MESSUNGEN_PRUEFUNGEN * 2;
            if (count > maxCount) {
              columns.splice(10 + maxCount, count - maxCount);
            }

            const ErsterEintrag = columns.find(column => column['bezeichnung'] === 'IST');
            this.ErsterEintragMessung = ErsterEintrag['id'];
            this.tables['messungen'].rows = table.rows;
            this.tables['messungen'].columns = columns;
            this.hasMessungen = this.tables['messungen'].rows.length > 0;
            break;
          case 'mangel':
            this.tables['mangel'].rows = elem.rows;
            this.tables['mangel'].columns ??= elem.columns;
            this.hasMangel = this.tables['mangel'].rows.length > 0;

            this.hasOpenMangel = this.tables['mangel'].rows.some((row: Row) => !row.auftrag && !row.erledigtStatus);
            break;
          case 'anlagenstatistik':
            this.anlagenstatistik = elem;
            this.tables[index] = elem;
            this.kavstatistic = elem['kav_ent'];
            this.graphkavelemente = [];
            this.kavelemente = [];
            this.fkninsp = elem['fk_n_insp'];
            this.fknih = elem['fk_n_ih'];

            this.dia_fkninsp = JSON.parse(JSON.stringify(this.graphfk));
            this.dia_fknih = JSON.parse(JSON.stringify(this.graphfk));
            this.dia_fkninsp.data = this.fkninsp;
            this.dia_fknih.data = this.fknih;
            let max = elem['maxfk'];
            this.dia_fkninsp.layout.yaxis = { range: [0, max] };
            this.dia_fknih.layout.yaxis = { range: [0, max] };

            let kavSpez = elem['kav_spez'];
            this.dia_kav_spez = JSON.parse(JSON.stringify(this.graphkav0));

            if (elem['kav_spez'].length != 0) {
              this.dia_kav_spez.data = elem['kav_spez'][0];
              this.dia_kav_spez.data[0].mode = 'lines+markers';
              this.dia_kav_spez.data[0].name = 'hv';
              this.dia_kav_spez.data[0].type = 'scatter';
              this.dia_kav_spez.data[0].line = {
                shape: 'hv',
                //color: 'rgb(219, 64, 82)',
                width: 2,
              };

              let bc1 = toColor(kavSpez[1].col1);
              let bc2 = toColor(kavSpez[1].col2);
              let bc3 = toColor(kavSpez[1].col3);

              this.dia_kav_spez.layout.yaxis = { range: [0, 1], dtick: 0.1 };
              //this.dia_kav_spez.layout.xaxis = { rangemode: "tozero"}
              this.dia_kav_spez.layout.shapes = [
                {
                  layer: 'below',
                  xref: 'paper',
                  yref: 'paper',
                  x0: 0,
                  x1: 1,
                  y0: 0,
                  y1: kavSpez[1].kat1,
                  type: 'rect',
                  fillcolor: bc1,
                  opacity: 0.4,
                },
                {
                  layer: 'below',
                  xref: 'paper',
                  yref: 'paper',
                  x0: 0,
                  x1: 1,
                  y0: kavSpez[1].kat1,
                  y1: kavSpez[1].kat3,
                  type: 'rect',
                  fillcolor: bc2,
                  opacity: 0.4,
                },
                {
                  layer: 'below',
                  xref: 'paper',
                  yref: 'paper',
                  x0: 0,
                  x1: 1,
                  y0: kavSpez[1].kat3,
                  y1: 1,
                  type: 'rect',
                  fillcolor: bc3,
                  opacity: 0.4,
                },
              ];

              this.graphkav0.layout.yaxis = { range: [0, 1], dtick: 0.1 };
              //this.graphkav0.layout.xaxis = { rangemode: "tozero"}
              this.graphkav0.layout.shapes = [
                {
                  layer: 'below',
                  xref: 'paper',
                  yref: 'paper',
                  x0: 0,
                  x1: 1,
                  y0: 0,
                  y1: kavSpez[1].kat1,
                  type: 'rect',
                  fillcolor: bc1,
                  opacity: 0.4,
                },
                {
                  layer: 'below',
                  xref: 'paper',
                  yref: 'paper',
                  x0: 0,
                  x1: 1,
                  y0: kavSpez[1].kat1,
                  y1: kavSpez[1].kat3,
                  type: 'rect',
                  fillcolor: bc2,
                  opacity: 0.4,
                },
                {
                  layer: 'below',
                  xref: 'paper',
                  yref: 'paper',
                  x0: 0,
                  x1: 1,
                  y0: kavSpez[1].kat3,
                  y1: 1,
                  type: 'rect',
                  fillcolor: bc3,
                  opacity: 0.4,
                },
              ];
            }
            this.ih_qoute = elem['ih_qoute'] || undefined;
            if (this.ih_qoute != undefined) {
              this.dia_ih_qoute = JSON.parse(JSON.stringify(this.graphfk));
              this.dia_ih_qoute.data = this.ih_qoute;
              this.dia_ih_qoute.layout.yaxis = { range: [0, 100] };
            }

            this.ih_average = elem['ih_average'] || undefined;
            if (this.ih_average != undefined) {
              this.dia_ih_average = JSON.parse(JSON.stringify(this.graphfk));
              this.dia_ih_average.data = this.ih_average;
            }

            let kav = elem['kav']?.filter(s => s.labels != null);
            let bcArr = elem['kav']?.filter(s => s.labels == null);
            let bc = toColor(bcArr[0]?.backcolor);

            for (let i = 0; i < kav?.length; i++) {
              let f = kav[i];

              f.hoverinfo = 'label+value';
              f.type = 'pie';
              f.hole = 0.8;
              f.textinfo = 'none';
              //f.marker.colors[0]='rgb(227, 234, 237)';
              //f.marker.colors[1]='rgb(0,121,173)';
              let av = [];
              av.push({ ...f });

              this.kavelemente.push(av);
              let dia = { ...this.graphkavdonat };
              dia.data = av;
              let v = Math.round(f.values[1] * 100);
              dia.layout.annotations[0].text = v.toString() + '%';
              dia.layout.title.text = this.mrTranslate.transform(f.name);

              if (f.name == 'Gesamt') {
                dia.layout.annotations[0].text = f.values[1].toFixed(3);
                //dia.layout.annotations[0].bgcolor = bc;
                dia.layout.annotations[0].bordercolor = bc;
                dia.layout.annotations[0].borderwidth = 2;
              } else {
                dia.layout.annotations[0].borderwidth = 0;
              }

              dia.layout.height = 100;
              dia.layout.width = 100;
              dia.layout.showlegend = false;

              let data = JSON.parse(JSON.stringify(dia));
              this.graphkavelemente.push(data);
            }

            break;
          default:
            if (elem && elem['type'] == 'table') {
              this.tables[index] = { rows: [], columns: elem.columns };
              setTimeout(() => {
                this.tables[index].rows = elem.rows;
              });
            } else {
              this.tables[index] = elem;
            }
            break;
        }
      });

      let activeTabExists = false;
      Object.keys(this.tables).forEach(tableKey => {
        const tab = tableKey == 'zaehler' ? 'messungen' : tableKey;
        if (this.activeTab[tab] && this.tables[tableKey]) {
          activeTabExists = true;
        }
      });
      if (!activeTabExists) {
        Object.keys(this.activeTab).forEach(tableKey => {
          if (tableKey == 'stamm') {
            this.activeTab[tableKey] = true;
          } else {
            this.activeTab[tableKey] = false;
          }
        });
      }
    }

    if (result['geometry']) {
      this.geometry = result['geometry'];
    }

    if (result['fehlerzeile']) {
      var v = getStammValue(result?.tabs?.stamm?.rows, 'StartMeter');
      var b = getStammValue(result?.tabs?.stamm?.rows, 'EndMeter');
      if (+v > +b) v = -v;
      this.progressData.emit({
        zeile: result['fehlerzeile'],
        stammid: this._ostammid,
        von: v,
        once: false,
      });
    } else {
      this.enlarge.emit({
        mode: 'progress',
        val: false,
      });
    }

    this.skizzeData = result['skizze'];
    this.skizzeName = result['skizzeName'];
    this.messungsOPRUEFID = result['messungsOPRUEFID'];
    this.hasGleismessung = result['hasgleismessung'];
    this.hasProfilmessung = result['hasprofilmessung'];

    if (this.tables['komponenten']) {
      let root = {
        id: 0,
        name: this.rootname,
        typ: null,
        selected: true,
        expanded: true,
        parent: null,
        rootpath: null,
        children: [],
      };

      for (let i = 0; i < this.komponentenObjekt.rows.length; i++) {
        let komp = this.komponentenObjekt.rows[i];

        if (komp.ParentID == 0) {
          let node: TreeDataNode = {
            id: komp.OKOMPID,
            name: komp.TypBezeichnung,
            typ: komp.KTYPID,
            selected: false,
            expanded: false,
            rootpath: null,
            parent: this.rootObjekt,
            children: [],
          };

          root.children.push(node);

          this.fillChildrenObjekt(node, komp.OKOMPID);
        }
      }

      this.rootObjekt = root;
    }

    this.spinner.disable();
    this.loading.next(false);
  }

  fillChildrenObjekt(parentNode: TreeDataNode, iid: number) {
    const filtered = this.komponentenObjekt.rows.filter(obj => {
      return obj.ParentID === iid;
    });

    for (let i = 0; i < filtered.length; i++) {
      let komp = filtered[i];
      let node: TreeDataNode = {
        id: komp.OKOMPID,
        name: komp.TypBezeichnung,
        typ: komp.KTYPID,
        selected: false,
        expanded: false,
        rootpath: null,
        parent: parentNode,
        children: [],
      };
      parentNode.children.push(node);

      this.fillChildrenObjekt(node, komp.OKOMPID);
    }
  }
  public getChildren = node => node.children;

  treeClick(value) {
    this.bilder = this.komponentenObjektBilder[value.id.toString()] || [];
    this.selOKOMPID = value.id;
    this.selnode = value;
    this.selnodename = value.name;
    this.selnodetyp = value.typ;
    this.spezData = [];
    this.kompHistoryData = [];

    Object.keys(this.komponentenObjektSpez).forEach(okompid => {
      if (okompid == this.selOKOMPID.toString()) {
        this.spezData = this.komponentenObjektSpez[okompid];
      }
    });

    Object.keys(this.komponentenObjektHist).forEach(okompid => {
      if (okompid == this.selOKOMPID.toString()) {
        this.kompHistoryData = this.komponentenObjektHist[okompid];
      }
    });
  }

  hideColumnsArr = ['bild'];
  get ostammid() {
    return this._ostammid;
  }

  previewMap(row: any) {
    this.showGIS = true;
    row.baseVon = this.baseStartMeter;
    if (row.position != null) {
      row.von = row.position;
      row.bis = row.position + 1;
    }
    setTimeout(() => {
      this.map = previewMap(row, this.geometry, this.map);
    }, 300);
  }

  protected async openHerstellModal() {
    if (!this.selectedSpez) {
      this.toastr.warning(this.mrTranslate.transform('Bitte zuerst eine Spezifikation auswählen'));
      if (!this.activeTab.spez) {
        for (const tab in this.activeTab) {
          this.activeTab[tab] = tab == 'spez';
        }
      }
      return;
    }

    const isLine = objtype.isLine(this.typ_Line_Point);
    this.selectedSpezID =
      isLine ?
        this.selectedSpez.OSPEZID.value
      : getSpezValue(Array.isArray(this.selectedSpez) ? this.selectedSpez : undefined, 'OSPEZID');

    if (!this.selectedSpezID) {
      return;
    }

    if (isLine && !(await this.sperrObjekt())) return;

    this.isHerstellDatenVisible = true;
  }

  protected closeHerstellModal(toReload: boolean) {
    if (objtype.isLine(this.typ_Line_Point)) firstValueFrom(this.apiService.entsperrAnlage(this.ostammid));

    setTimeout(() => (this.isHerstellDatenVisible = false));
    if (toReload) this.loadAnlageById(this.ostammid);
  }

  async getBauwerke() {
    if (!(await this.sperrObjekt())) return;

    this.overlayService.setOverlay({
      overlay: 'bauwerke',
      isTopOverlay: true,
      table: this.tables['bauwerke'],
      type: this.typ_Line_Point,
      otypid: this.otypid,
      ostamm: this.ostammid,
      stammData: this.linieValues,
      dataRefresh: this.dataRefresh,
    });
  }

  getBahnuebergaenge() {
    this.overlayService.setOverlay({
      overlay: 'bahnuebergang',
      opruefid: this.opruefid,
      excelHeader: {
        bezeichnung: getStammValue(this.tables['stamm']?.rows, 'Bezeichnung'),
        bezirk: getStammValue(this.tables['stamm']?.rows, 'Bezirk'),
        artDerStrasse: getSpezValue(
          Array.isArray(this.selectedSpez) ? this.selectedSpez : undefined,
          'Art des Straße'
        ),
      },
    });
  }

  async getEreignis() {
    if (!(await this.sperrObjekt())) return;

    this.overlayService.setOverlay({
      overlay: 'ereignisdialog',
      isTopOverlay: true,
      table: this.tables['ereignis'],
      type: this.typ_Line_Point,
      name: this.bezeichnung,
      otypid: this.otypid,
      ostamm: this.ostammid,
      stammData: this.linieValues,
      dataRefresh: this.dataRefresh,
    });
  }

  async getNaturschutz() {
    if (!(await this.sperrObjekt())) return;

    this.overlayService.setOverlay({
      overlay: 'naturschutz',
      isTopOverlay: true,
      table: this.tables['naturschutz'],
      type: this.typ_Line_Point,
      name: this.bezeichnung,
      otypid: this.otypid,
      ostamm: this.ostammid,
      stammData: this.linieValues,
      dataRefresh: this.dataRefresh,
    });
  }

  ngOnDestroy() {
    this.basicStore.setDetailViewStore({
      activeTab: this.activeTab,
    });
    this.refreshSubscription.unsubscribe();
  }

  graphDetail(graph_slug) {
    this.overlayService.setOverlay({
      overlay: 'messungDetail',
      id: this._ostammid,
      name: this.bezeichnung,
      graph: {
        layout: this.graph.layout,
        config: this.graph.config,
        m: this.graph.m,
        y: this.graph.yData[graph_slug],
      },
    });
  }

  resizeGrid() {
    setTimeout(() => {
      this.grid.resize();
    });
  }

  graphXY(x, y) {
    let obj = {
      x: x,
      y: y,
    };
    let arr = [obj];
    return arr;
  }

  ngAfterViewInit(): void {
    this.activeTab = {
      stamm: false,
      spez: false,
      bauwerke: false,
      mangel: false,
      messungen: false,
      historie: false,
      statistik: false,
    };
    let detailviewStore = this.basicStore.getDetailViewStore();
    if (detailviewStore) {
      if (detailviewStore['activeTab']) {
        this.activeTab = detailviewStore['activeTab'];
      }
    }
  }

  ngOnInit() {
    this.isRep = this.apiService.isRep;
    this.refreshSubscription = this.dataRefresh?.pipe().subscribe(() => {
      this.spinner.enable();
      this.loadAnlageById(this._ostammid);
      // Refresh Tabs (Anlagen, Mängel u. Aufträge) im ComponentStore
      this.basicStore.reloadTabs({ emitter: this.reloadTabsIsReady });

      // Refresh Aktuelle Tab vom ComponentStore gleich nach Fertigkeit
      this.reload.next(this.reloadTabsIsReady);
    });
  }

  addKategorie() {
    this.TitelKat = this.mrTranslate.transform('Neue Kategorie');
    this.kategorie.nativeElement.value = '';
    this.isKatVisible = true;
    this.katStatus = 'add';
  }

  editKategorie() {
    this.TitelKat = this.mrTranslate.transform('Kategorie bearbeiten');
    this.kategorie.nativeElement.value = this.docheader[this.seldoctyp];
    this.isKatVisible = true;
    this.katStatus = 'edit';
  }

  async delKategorie() {
    const ok = await this.confirmService.danger(
      this.mrTranslate.transform('Sind Sie sicher, dass Sie diese Kategorie löschen möchten?')
    );
    if (ok) {
      this.katStatus = 'del';
      this.docheader.splice(this.seldoctyp, 1);
      this.dokheaderAnzahlItems.splice(this.seldoctyp, 1);
      this.sendKategorie();
    }
  }

  saveKategorie() {
    let valKat = this.kategorie.nativeElement.value;
    if (valKat == '') return;

    if (this.katStatus == 'edit' && valKat != this.docheader[this.seldoctyp]) {
      this.docheader[this.seldoctyp] = valKat;
      this.sendKategorie();
    }

    if (this.katStatus == 'add') {
      this.docheader.push(valKat);
      this.dokheaderAnzahlItems.push(0);
      this.sendKategorie();
    }
  }

  sendKategorie() {
    let upfile = {
      ostammid: this._ostammid,
      kategorie: this.docheader.join(';'),
    };

    this.apiService
      .editAnlagenDokumentKategorie(JSON.parse(JSON.stringify(upfile)))
      .pipe(first())
      .subscribe((res: any) => {
        if (res) {
          this.isKatVisible = false;
          setTimeout(() => {
            this.doctabChange(0);
          }, 75);
        } else {
          this.toastr.error(this.mrTranslate.transform('Error') + ': ' + res.error);
        }
      });
  }

  doctabChange(event) {
    this.docs.rows = [];
    this.seldoctyp = event;
    this.docs.rows = this.docsorginal.rows.filter(item => item.Kategorie == this.seldoctyp);
    this.selAnzDok = this.dokheaderAnzahlItems[this.seldoctyp];
  }

  readonly param = input('file');
  readonly accept = input('image/png, image/jpeg, application/pdf');
  onDrop: any;
  uploadfiles: any[] = [];
  selecteddocrow: any;

  rowDocChanged(value: any) {
    this.selecteddocrow = value;
  }

  openDokument(row: any) {
    let openByFileType = async (data: Blob) => {
      let dataIn = {
        data: data,
        titel: this.mrTranslate.transform('Datei') + ': ' + this.selecteddocrow.Name,
        file: this.selecteddocrow.Name,
      };

      let t = this.selecteddocrow.Name.split('.').pop();

      if (t.toLowerCase() == 'pdf') {
        this.overlayService.setOverlay({
          overlay: 'viewAuftrag',
          data: dataIn,
          zIndex: 5,
        });
      } else {
        let b: string = Buffer.from(await data.arrayBuffer()).toString('base64');
        this.dokumentenMedia.set({
          title: 'Dokumentendatei',
          files: [{ base64: b, extension: t, name: this.selecteddocrow.Name }],
          isSkizze: false,
        });
      }
    };

    this.selecteddocrow = row;

    if (this.selecteddocrow.data) {
      openByFileType(this.selecteddocrow.data);
    } else {
      this.apiService
        .getDocumentFile(this.selecteddocrow.ID)
        .pipe(first())
        .subscribe(res => {
          if (!res.toString().includes('Fehler')) {
            let data = this.apiService.convertFileAsBlob(res, this.selecteddocrow.Name, 512);

            if (this.hideColumnsArr.includes('data') === undefined) this.hideColumnsArr.push('data');

            this.selecteddocrow.data = data;
            openByFileType(data);
          } else {
            this.toastr.error(this.mrTranslate.transform(res.toString()));
          }
        });
    }
  }

  async deleteDokument(row: any) {
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie dieses Dokument löschen möchten?');
    const ok = await this.confirmService.danger(text);
    if (ok) {
      this.selecteddocrow = row;
      this.uploadfiles = [];

      let upfile = {
        status: 'delete',
        id: this.selecteddocrow.ID,
        ostammid: this._ostammid,
        kategorie: this.seldoctyp,
        filename: '',
        size: 0,
        typ: '',
        base64: '',
        extension: '',
      };

      this.uploadfiles.push(upfile);

      this.apiService
        .addAnlageDocument(JSON.parse(JSON.stringify(this.uploadfiles)))
        .pipe(first())
        .subscribe((res: any) => {
          if (res?.doks) {
            this.docsorginal = res.doks;
            this.docs.columns = res.doks.columns;
            this.docs.rows = res.doks.rows.filter(item => item.Kategorie == this.seldoctyp);
            this.dokheaderAnzahlItems = res.dokheaderAnzahlItems;

            setTimeout(() => {
              this.doctabChange(this.seldoctyp);
            }, 75);
          } else {
            this.toastr.error(this.mrTranslate.transform('Error') + ': ' + res.error);
          }
        });
    }
  }

  addDok() {
    const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;

    this.uploadfiles = [];

    fileUpload.onchange = () => {
      let files: FileList = fileUpload.files;
      for (const file of Array.from(files)) {
        const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];

        if (!allowedTypes.includes(file.type.toLowerCase())) {
          this.toastr.warning(
            this.mrTranslate.transform('Dateiformat ungültig. Nur pdf, png oder jpg-Formate erlaubt')
          );
          continue;
        }

        const blob: Blob = file;
        blob.arrayBuffer().then(b => {
          //let base64 =  btoa(String.fromCodePoint(...new Uint8Array(b)));
          let base64 = this.arrayBufferToBase64(b);
          let upfile = {
            status: 'add',
            id: -1,
            ostammid: this._ostammid,
            kategorie: this.seldoctyp,
            filename: file.name,
            size: file.size,
            typ: file.type,
            base64: base64,
            extension: this.getFileExtension(file.name),
          };

          this.uploadfiles.push(upfile);

          if (fileUpload.files.length == this.uploadfiles.length) {
            this.apiService
              .addAnlageDocument(structuredClone(this.uploadfiles))
              .pipe(first())
              .subscribe((res: any) => {
                if (res?.doks) {
                  this.docsorginal = res.doks;
                  this.docs.columns = res.doks.columns;
                  this.docs.rows = res.doks.rows.filter(item => item.Kategorie == this.seldoctyp);
                  this.dokheaderAnzahlItems = res.dokheaderAnzahlItems;

                  setTimeout(() => {
                    this.doctabChange(this.seldoctyp);
                  }, 75);
                } else {
                  this.toastr.error(this.mrTranslate.transform('Error') + ': ' + res.error);
                }
              });
          }
        });
      }
    };
    fileUpload.click();
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000; // 32KB chunks

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const subarray = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...subarray);
    }

    return btoa(binary);
  }

  getFileExtension(filename) {
    return /[.]/.exec(filename) ? /[^.]+$/.exec(filename)[0] : undefined;
  }

  tabChange(tab: string) {
    this.initTab[tab] = true;
    setTimeout(() => {
      if (tab == 'paint') this.expand('full', true);
      if (this.grid) this.grid.resize();
    });
  }
  protected readonly resize$ = new Subject<void>();
  expand(mode: string, val?: boolean) {
    if (mode == 'full') {
      if (val != this.expanded) {
        this.expanded = !this.expanded;
        this.enlarge.emit({
          mode: 'full',
          val: this.expanded,
        });
      }
    }
    if (mode == 'half') {
      this.split50 = !this.split50;
      this.enlarge.emit({
        mode: 'half',
        val: this.split50,
      });
    }
    this.resize$.next();
    let save = [this.graph.config, this.graph.layout];
    this.graph.config = [];
    this.graph.layout = [];
    this.graph.config = save[0];
    this.graph.layout = save[1];
  }

  getMessungen() {
    this.spinner.enable();
    this.overlayService.setOverlay({
      overlay: 'openMeasurements',
      id: this._ostammid,
      name: this.bezeichnung,
    });
  }

  getMessungenLWM() {
    this.spinner.enable();
    this.overlayService.setOverlay({
      overlay: 'openMessungLWM',
      name: this.bezeichnung,
      id: this._ostammid,
      opruefid: this.opruefid,
    });
  }

  getKomponenten() {
    this.overlayService.setOverlay({
      overlay: 'komponenteneditdialog',
      id: this.ostammid,
      otypid: this.otypid,
      stamm: this.tables['stamm'],
      type: this.typ_Line_Point,
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }

  saveMangelBemerkung() {
    this.showbemerkung = false;
    var req = {
      bemerkung: this.mangelbemerkung,
    };
    this.apiService
      .saveMangelBemerkung(req, this.curID)
      .pipe(first())
      .subscribe(() => {
        setTimeout(() => {
          this.dataRefresh?.emit(true);
        });
      });
    this.curID = 0;
    this.mangelbemerkung = '';
  }

  openBemerkung(bemerkung: string, id: number) {
    this.mangelbemerkung = bemerkung;
    this.curID = id;
    this.showbemerkung = true;
  }

  openSonderinspektion(row: any) {
    let inData = {
      Status: 1,
      OSTAMMID: row.ostammid,
      OMAZSID: row.OMAZSID,
      OTYPID: this.otypid,
      OSISPKID: 0,
      Mangel: row.mangel,
      aktive: true,
      intervall: 10,
    };
    this.modal.open(inData);

    this.modal.onOK.subscribe(res => {
      if (res == true) {
        this.modal.close();
        this.dataRefresh?.emit(true);
      }
    });
  }

  getMangelverteilung() {
    this.overlayService.setOverlay({
      overlay: 'mangelverteilung',
      id: this.ostammid,
      stamm: this.tables['stamm'],
      mangel: this.tables['mangel'],
      spez: this.tables['spez'],
      bauwerk: this.tables['bauwerke'],
      zIndex: 8,
    });
  }

  setTrassierung() {
    this.overlayService.setOverlay({
      overlay: 'trassierungseditor',
      id: this.ostammid,
      typid: this.otypid,
      zIndex: 8,
    });
  }

  getProfile() {
    this.overlayService.setOverlay({
      overlay: 'profileviewer',
      id: this._ostammid,
      name: this.bezeichnung,
      rechte: this.authService?.token?.rights?.status,
      start: 0,
    });
    //this.enlargePB.emit(true);
  }

  getMessungsUebersicht() {
    this.overlayService.setOverlay({
      overlay: 'openMessungsuebersicht',
      id: this.ostammid,
      pruefid: this.opruefid,
      bild: this.strBild,
      LPDatum: this.lpDatum,
      zIndex: 8,
    });
  }

  downloadExcelFile() {
    if (this.excelPath) {
      this.apiService
        .downloadExcelFile(this.excelPath)
        .pipe(first())
        .subscribe(res => {
          if (res) {
            let filename = this.excelPath.split('/');
            filename = filename[filename.length - 1] + '.xlsx';
            this.apiService.downloadBlobAsFile(res, filename);
          }
        });
    }
  }

  async sendSAP() {
    if (this.opruefid != 0) {
      this.spinner.enable();

      const res = await firstValueFrom(this.apiService.setSAPExport(this.opruefid));

      this.spinner.disable();
      if (res['success']) {
        if (res['error'].length) {
          let txt = 'Es wurden 1 Inspektionen exportiert.\nAnzahl übertragener Mängel: ' + res['done'] + '\n\n';
          txt += 'Fehler:\n';
          txt += res['error'].join('\n ');
          await this.confirmService.warning(this.mrTranslate.transform(txt), false);
        } else {
          let txt = 'Es wurden 1 Inspektionen exportiert.\n\n Anzahl Mängel: ' + res['done'];
          await this.confirmService.info(this.mrTranslate.transform(txt), false);
        }
      } else {
        this.toastr.error(this.mrTranslate.transform(res['error']));
      }
    }
  }

  getPDFFile() {
    if (this.opruefid != 0) {
      this.spinner.enable();
      this.apiService
        .getPDFFile(this.opruefid)
        .pipe(first())
        .subscribe(res => {
          if (res) {
            this.overlayService.setOverlay({
              overlay: 'viewAuftrag',
              titel: this.mrTranslate.transform('Inspektionsbericht'),
              data: res,
              zIndex: 5,
            });
          } else {
            this.toastr.warning(this.mrTranslate.transform('Kein PDF erstellt, Fehler im Log.'));
          }
          this.spinner.disable();
        });
    } else this.toastr.warning(this.mrTranslate.transform('Für diese Objekt sind keine Inspektionsdaten vorhanden'));
  }

  getRailmonitor() {
    this.overlayService.setOverlay({
      overlay: 'railmonitor',
      id: this._ostammid,
      name: this.bezeichnung,
      start: 0,
    });
  }

  openStellkraftmessung() {
    this.overlayService.setOverlay({
      overlay: 'stellkraftmessung',
      id: this._ostammid,
      type: 'standard',
    });
  }

  openStellkraftCMS() {
    this.overlayService.setOverlay({
      overlay: 'stellkraftmessung',
      id: this._ostammid,
      type: 'cms',
    });
  }

  createAssignment() {
    if (this.tables?.mangel?.rows?.length) {
      this.overlayService.setOverlay({
        overlay: 'createassignment',
        typ_Line_Point: this.typ_Line_Point,
        id: this._ostammid,
        Opt_F_Drawing: this.objOpt?.Opt_F_Drawing,
        latlng:
          getStammValue(this.tables?.stamm?.rows, 'GPS_Lat') +
          ',' +
          getStammValue(this.tables?.stamm?.rows, 'GPS_Lon'),
        // this.tables["stamm"]["rows"][7]["Bezeichnung"],
        zIndex: 5,
        dataRefresh: this.dataRefresh,
      });
    }
  }

  createDefect() {
    const { startmeter, endmeter } = this.linieValues;

    this.overlayService.setOverlay({
      overlay: 'createDefect',
      typ_Line_Point: this.typ_Line_Point,
      id: this._ostammid,
      startmeter,
      endmeter,
      zIndex: 5,
      dataRefresh: this.dataRefresh,
    });
  }

  createBlocking() {
    const { startmeter, endmeter } = this.linieValues;

    this.overlayService.setOverlay({
      overlay: 'createBlocking',
      id: this._ostammid,
      typ_Line_Point: this.typ_Line_Point,
      pointtype: this.pointtype ? this.pointtype : undefined,
      startmeter,
      endmeter,
      zIndex: 5,
      dataRefresh: this.dataRefresh,
    });
  }

  openNewLA(row: any) {
    const { startmeter, endmeter } = this.linieValues;
    this.overlayService.setOverlay({
      overlay: 'createLaStelle',
      id: this._ostammid,
      omazsid: row.OMAZSID,
      typ_Line_Point: this.typ_Line_Point,
      pointtype: this.pointtype,
      startmeter,
      endmeter,
      zIndex: 5,
      dataRefresh: this.dataRefresh,
    });
  }

  createLAStelle() {
    const { startmeter, endmeter } = this.linieValues;

    this.overlayService.setOverlay({
      overlay: 'createLaStelle',
      id: this._ostammid,
      name: this.bezeichnung,
      omazsid: 0,
      typ_Line_Point: this.typ_Line_Point,
      pointtype: this.pointtype,
      startmeter,
      endmeter,
      zIndex: 5,
      dataRefresh: this.dataRefresh,
    });
  }

  async createSpezifikationen() {
    if (!(await this.sperrObjekt())) return;

    this.overlayService.setOverlay({
      overlay: 'spezifikation',
      ostamm: this.ostammid,
      name: this.bezeichnung,
      otypid: this.otypid,
      type: this.typ_Line_Point,
      stammData: this.linieValues,
      dataRefresh: this.dataRefresh,
    });
  }

  private get linieValues() {
    const startmeter = getStammValue(this.tables?.stamm?.rows, 'StartMeter');
    const endmeter = getStammValue(this.tables?.stamm?.rows, 'EndMeter');
    return { startmeter, endmeter };
  }

  async sperrObjekt(): Promise<boolean> {
    if (this.isRep) return true;

    const { success, error, username } = await firstValueFrom(this.apiService.sperrAnlage(this.ostammid));

    if (!success) {
      console.error(error);
      this.toastr.error(
        this.mrTranslate.transform('Ein Fehler ist aufgetreten'),
        this.mrTranslate.transform('Objekt gesperrt')
      );
      return false;
    } else if (username) {
      this.toastr.info(
        this.mrTranslate.transform('Stammdokument wird bei {0} bearbeitet.').replace('{0}', username),
        this.mrTranslate.transform('Objekt gesperrt')
      );
      return false;
    } else {
      // * Register ostammid, um beim Destroy oder Schliessen zu entsperren
      this.entsperrService.ostammid.set(this.ostammid);
      return true;
    }
  }

  async createInspektion(edit: boolean) {
    if (!(await this.sperrObjekt())) return;

    this.overlayService.setOverlay({
      overlay: 'openInspektionsDialog',
      isTopOverlay: true,
      stammData: this.linieValues,
      otypid: this.otypid,
      kpruvid: this.KPRUVID(),
      name: this.bezeichnung,
      type: this.typ_Line_Point,
      pruefid: this.opruefid,
      ostamm: this.ostammid,
      messungen: this.tables['messungen'],
      skizze: this.skizzeName,
      messungsOPRUEFID: this.messungsOPRUEFID,
      isDevEdit: edit,
      dataRefresh: this.dataRefresh,
    });
  }

  createFeedback() {
    if (this.opruefid > 0) {
      this.overlayService.setOverlay({
        overlay: 'createfeedback',
        id: this._ostammid,
        stamm: this.tables['stamm'],
        mangel: this.tables['mangel'],
        name: this.bezeichnung,
        typ_Line_Point: this.typ_Line_Point,
        otypod: this.otypid,
        zIndex: 5,
        Opt_F_Drawing: this.objOpt?.Opt_F_Drawing,
        dataRefresh: this.dataRefresh,
      });
    }
  }

  async editStammdaten() {
    if (!(await this.sperrObjekt())) return;

    this.overlayService.setOverlay({
      overlay: 'openStammdatenDialog',
      id: this._ostammid,
      otypid: this.otypid,
      type: this.typ_Line_Point,
      opruefid: this.opruefid,
      zIndex: 5,
      dataRefresh: this.dataRefresh,
    });
  }

  formatter(value) {
    var d = new Date(Date.parse(value));
    return d.getDate() + '.' + d.getMonth() + '.' + d.getFullYear();
  }

  /* MR 2024-08-07 jetzt unter Rückmeldung
  createMangel(event) {
    const { startmeter, endmeter } = this.linieValues;

    this.overlayService.setOverlay({
      overlay: "mangelerfassung",
      id: {
        ostammid: this._ostammid,
        typ_Line_Point: this.typ_Line_Point,
        otypid: this.otypid,
        mangel: this.tables?.mangel?.rows?.length ? this.tables["mangel"] : undefined,
        startmeter,
        endmeter,
      },
      zIndex: 5,
      dataRefresh: this.dataRefresh,
    });
  }
  */

  closeHandler() {
    this.split50 = false;
    this.expanded = false;
    this.progressbarenlarged = false;
    this.enlarge.emit({
      mode: 'close',
      val: false,
    });
  }

  protected openPdf(file: File) {
    this.overlayService.setOverlay({
      overlay: 'viewAuftrag',
      isTopOverlay: true,
      data: {
        data: file,
        titel: file?.name || this.mrTranslate.transform('Datei'),
        file: file?.name + '.pdf',
      },
      zIndex: 5,
    });
  }
}
